import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Folder, MoreHorizontal, Pencil, FolderInput, Trash2 } from 'lucide-react'
import type { Folder as FolderType } from '../../types'
import type { FolderAction } from './FolderTree'

interface FolderRowProps {
  folder: FolderType
  projectId: string
  onAction?: (action: FolderAction) => void
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function FolderRow({ folder, projectId, onAction }: FolderRowProps) {
  const navigate = useNavigate()
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    if (showMenu) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  return (
    <div
      onClick={() => navigate(`/project/${projectId}/folder/${folder.folder_id}`)}
      className="light-card p-4 hover:border-iflow-300 hover:shadow-sm transition-all cursor-pointer flex items-center gap-4"
    >
      <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
        <Folder size={16} className="text-amber-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 truncate">{folder.name}</h4>
        {folder.created_at && (
          <p className="text-xs text-gray-500 mt-0.5">{formatDate(folder.created_at)}</p>
        )}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="hidden sm:inline-flex px-2 py-0.5 bg-amber-50 text-amber-600 text-xs rounded-full">
          Folder
        </span>
        {onAction && (
          <div ref={menuRef} className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu) }}
              className="p-1 text-gray-500 hover:text-gray-600 rounded hover:bg-sky-500/10"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-8 light-card shadow-lg py-1 z-10 w-36">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onAction({ type: 'rename', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30"
                >
                  <Pencil size={14} /> Rename
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onAction({ type: 'move', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-white/30"
                >
                  <FolderInput size={14} /> Move to...
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowMenu(false); onAction({ type: 'delete', folder }) }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-white/30"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
